import { useState } from 'react';
import { CheckCircle, XCircle, Eye, ChevronDown, ChevronUp, Camera, MessageSquare, Image, Video, Mic } from 'lucide-react';
import EvidenceCapture from './EvidenceCapture';

type Score = 'competent' | 'not_yet' | 'not_observed';

interface EvidenceItem {
  id: string;
  criterionIndex: number;
  type: string;
  fileUrl: string;
  caption: string;
  transcriptText: string;
  createdAt: string;
}

interface CriterionCardProps {
  criterion: { index: number; label: string; evidenceRule: string };
  score?: Score;
  evidence: EvidenceItem[];
  canAssess: boolean;
  sessionId: string;
  onScore: (score: Score) => void;
  onEvidenceSaved: () => void;
}

const scoreConfig: Record<Score, { label: string; icon: typeof CheckCircle; color: string; bg: string }> = {
  competent: { label: 'Competent', icon: CheckCircle, color: 'text-primary', bg: 'bg-primary/20 border-primary/30' },
  not_yet: { label: 'Not Yet', icon: XCircle, color: 'text-destructive', bg: 'bg-destructive/20 border-destructive/30' },
  not_observed: { label: 'Not Observed', icon: Eye, color: 'text-muted-foreground', bg: 'bg-muted border-border' },
};

export default function CriterionCard({ criterion, score, evidence, canAssess, sessionId, onScore, onEvidenceSaved }: CriterionCardProps) {
  const [expanded, setExpanded] = useState(false);
  const [showEvidence, setShowEvidence] = useState(false);

  const cfg = score ? scoreConfig[score] : null;
  const hasEvidence = evidence.length > 0;

  return (
    <div className={`border rounded-xl overflow-hidden transition-all ${
      cfg ? cfg.bg : 'bg-card border-border'
    }`}>
      {/* Main row */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-start gap-3 p-3.5 text-left touch-manipulation"
      >
        <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${
          cfg ? '' : 'bg-muted'
        }`}>
          {cfg ? <cfg.icon className={`w-4 h-4 ${cfg.color}`} /> :
           <span className="text-xs font-bold text-muted-foreground">{criterion.index + 1}</span>}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-foreground leading-snug">{criterion.label}</p>
          {criterion.evidenceRule && (
            <p className="text-[11px] text-muted-foreground mt-0.5 italic">{criterion.evidenceRule}</p>
          )}
          {cfg && (
            <p className={`text-[11px] font-semibold mt-1 ${cfg.color}`}>{cfg.label}</p>
          )}
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {hasEvidence && (
            <span className="flex items-center gap-0.5 text-[10px] text-muted-foreground">
              <Image className="w-3 h-3" />{evidence.length}
            </span>
          )}
          {expanded ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
        </div>
      </button>

      {/* Expanded content */}
      {expanded && (
        <div className="px-3.5 pb-3.5 space-y-3">
          {/* Score buttons */}
          {canAssess && (
            <div className="grid grid-cols-3 gap-2">
              {(['competent', 'not_yet', 'not_observed'] as Score[]).map(s => {
                const c = scoreConfig[s];
                const isActive = score === s;
                return (
                  <button
                    key={s}
                    onClick={() => onScore(s)}
                    className={`flex flex-col items-center gap-1 p-2.5 rounded-xl border-2 transition-all touch-manipulation ${
                      isActive ? c.bg : 'bg-card border-border hover:border-muted-foreground/30'
                    }`}
                  >
                    <c.icon className={`w-5 h-5 ${isActive ? c.color : 'text-muted-foreground'}`} />
                    <span className={`text-[10px] font-bold uppercase tracking-wider ${isActive ? c.color : 'text-muted-foreground'}`}>
                      {c.label}
                    </span>
                  </button>
                );
              })}
            </div>
          )}

          {/* Evidence list */}
          {hasEvidence && (
            <div className="space-y-1.5">
              <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">Evidence ({evidence.length})</p>
              {evidence.map(e => (
                <div key={e.id} className="flex items-center gap-2 p-2 bg-background/50 rounded-lg">
                  {e.type === 'Photo' ? <Camera className="w-3.5 h-3.5 text-muted-foreground shrink-0" /> :
                   e.type === 'Video' ? <Video className="w-3.5 h-3.5 text-muted-foreground shrink-0" /> :
                   e.type === 'Voice' ? <Mic className="w-3.5 h-3.5 text-muted-foreground shrink-0" /> :
                   <MessageSquare className="w-3.5 h-3.5 text-muted-foreground shrink-0" />}
                  <div className="flex-1 min-w-0">
                    {e.fileUrl && e.type === 'Photo' && (
                      <a href={e.fileUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline">View photo</a>
                    )}
                    {e.fileUrl && e.type === 'Video' && (
                      <a href={e.fileUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline">View video</a>
                    )}
                    {e.caption && <p className="text-xs text-foreground truncate">{e.caption}</p>}
                    {e.transcriptText && <p className="text-xs text-foreground truncate">{e.transcriptText}</p>}
                    {!e.caption && !e.transcriptText && !e.fileUrl && <p className="text-xs text-muted-foreground">Evidence attached</p>}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Add evidence button */}
          {canAssess && (
            <>
              {!showEvidence ? (
                <button
                  onClick={() => setShowEvidence(true)}
                  className="flex items-center gap-1.5 text-xs text-primary font-medium touch-manipulation"
                >
                  <Camera className="w-3.5 h-3.5" /> Add evidence
                </button>
              ) : (
                <EvidenceCapture
                  sessionId={sessionId}
                  criterionIndex={criterion.index}
                  onSaved={() => { setShowEvidence(false); onEvidenceSaved(); }}
                  onCancel={() => setShowEvidence(false)}
                />
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
